import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentPlanModel } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { CaretDownIconSmall, Text, CheckIconSmall, Button } from '@frontend/design-system';
import { usePaymentPlanDetails } from '../../hooks/payment-plans/use-payment-plan-details';
import { usePaymentPlanShallowStore } from '../../providers/payment-plans-provider';
import { formatCentsToCurrency } from '../../utils/currency-helpers';

type TextDropdownProps = {
  label: string;
  options: PaymentPlanModel[];
  paymentPlanId: string;
  trackingId?: string;
};

export const TextDropdown = ({ label, options, trackingId, paymentPlanId }: TextDropdownProps) => {
  const { t } = useTranslation('payments');
  const navigate = useNavigate();
  const { paymentPlanId: planId } = usePaymentPlanDetails({ paymentPlanId });
  const { setSelectedPaymentPlan } = usePaymentPlanShallowStore('setSelectedPaymentPlan');
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [reducedOptions, setReducedOptions] = useState<PaymentPlanModel[]>([]);
  const [showAll, setShowAll] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const reduceOptions = () => {
    let optionsArr = options.sort((a, b) => b.schedule.amount - a.schedule.amount);
    const selectedOptionIndex = optionsArr.findIndex((option) => option.id === planId);
    const option = optionsArr.splice(selectedOptionIndex, 1);

    if (option[0]) {
      optionsArr.unshift(option[0]);
    }

    if (!showAll && optionsArr.length > 2) {
      optionsArr = optionsArr.slice(0, 2);
    }
    setReducedOptions(optionsArr || []);
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current?.contains(e.target as HTMLDivElement)) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    reduceOptions();
  }, [options, showAll, planId]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div ref={dropdownRef}>
      <div
        css={css`
          display: flex;
        `}
      >
        <Button
          variant='tertiary'
          trackingId={trackingId}
          css={css`
            display: flex;
            flex-flow: row;
            gap: ${theme.spacing(1)};
          `}
          disabled={reducedOptions.length <= 1}
          onClick={() => {
            setShowOptions(!showOptions);
            setShowAll(false);
          }}
        >
          {label}
          <CaretDownIconSmall />
        </Button>
      </div>
      {showOptions && (
        <div
          css={css`
            position: absolute;
            background-color: ${theme.colors.white};
            z-index: 99;
            width: max-content;
            border-radius: ${theme.borderRadius.medium};
            box-shadow: ${theme.shadows.light};
            padding: ${theme.spacing(2, 1)};
          `}
        >
          {reducedOptions.map((option) => (
            <Button
              variant='secondary'
              trackingId={`${trackingId}-option`}
              key={option.id}
              css={css`
                display: flex;
                flex-flow: row;
                justify-content: space-between;
                min-width: 300px;
                width: 100%;
                border: transparent;
              `}
              onClick={() => {
                setSelectedPaymentPlan(option);
                setShowOptions(false);
                navigate({ to: '/payments/payment-plans/$planId', params: { planId: option.id } });
              }}
            >
              <div
                css={css`
                  display: flex;
                  flex-flow: row;
                  gap: ${theme.spacing(1)};
                  align-items: center;
                `}
              >
                <CheckIconSmall
                  css={css`
                    ${option.id !== planId && 'visibility: hidden;'}
                  `}
                />
                <Text
                  css={css`
                    max-width: 150px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                  `}
                >
                  {option.name}
                </Text>
              </div>
              {Number(option.schedule.amount) === 0 ? (
                <Text
                  css={css`
                    padding: ${theme.spacing(0.25, 1)};
                    background-color: ${theme.colors.success10};
                    color: ${theme.colors.success50};
                    font-size: ${theme.fontSize(14)};
                  `}
                >
                  {formatCentsToCurrency(option.schedule.amount)}
                </Text>
              ) : (
                <Text
                  css={css`
                    color: ${theme.colors.neutral40};
                  `}
                >
                  {formatCentsToCurrency(option.schedule.amount)}
                </Text>
              )}
            </Button>
          ))}
          {!showAll && options.length > 2 && (
            <Button
              trackingId={`${trackingId}-btn-viewall`}
              variant='tertiary'
              css={css`
                width: 100%;
                margin-top: ${theme.spacing(1)};
              `}
              onClick={() => setShowAll(true)}
            >
              {t('View all plans')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
